/**
 * Routes et contrôle d'accès côté interface. Les éléments réservés sont masqués
 * ici, mais les droits restent appliqués côté serveur (règles et API).
 */
import { createRouter, createWebHistory, type RouteLocationNormalized } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import type { Role } from '@/types'

declare module 'vue-router' {
  interface RouteMeta {
    /** Rôle minimal ; absent = page publique. */
    minRole?: Exclude<Role, ''>
    /** Pages hors coque applicative (vitrine, connexion). */
    bare?: boolean
    /** Masque la barre d'onglets mobile (fiche détail avec barre d'action). */
    hideTabs?: boolean
    title?: string
  }
}

const router = createRouter({
  history: createWebHistory(),
  scrollBehavior: (to, _from, saved) => saved ?? (to.hash ? { el: to.hash } : { top: 0 }),
  routes: [
    { path: '/', name: 'showcase', component: () => import('@/views/ShowcaseView.vue'), meta: { bare: true } },
    { path: '/connexion', name: 'login', component: () => import('@/views/LoginView.vue'), meta: { bare: true, title: 'Connexion' } },
    {
      path: '/catalogue',
      name: 'catalog',
      component: () => import('@/views/CatalogView.vue'),
      meta: { minRole: 'member', title: 'Catalogue' },
    },
    {
      path: '/livres/:id',
      name: 'book',
      component: () => import('@/views/BookDetailView.vue'),
      props: true,
      meta: { minRole: 'member', hideTabs: true },
    },
    {
      path: '/livres/:id/modifier',
      name: 'book-edit',
      component: () => import('@/views/BookEditView.vue'),
      props: true,
      meta: { minRole: 'editor', hideTabs: true, title: 'Modifier' },
    },
    {
      path: '/ajouter',
      name: 'add',
      component: () => import('@/views/AddBookView.vue'),
      meta: { minRole: 'editor', hideTabs: true, title: 'Ajouter un livre' },
    },
    {
      path: '/emprunts',
      name: 'my-loans',
      component: () => import('@/views/MyLoansView.vue'),
      meta: { minRole: 'member', title: 'Mes emprunts' },
    },
    {
      path: '/qui-a-quoi',
      name: 'who-has-what',
      component: () => import('@/views/WhoHasWhatView.vue'),
      meta: { minRole: 'editor', title: 'Qui a quoi' },
    },
    {
      path: '/administration',
      name: 'admin',
      component: () => import('@/views/AdminView.vue'),
      meta: { minRole: 'superadmin', title: 'Administration' },
    },
    { path: '/compte', name: 'account', component: () => import('@/views/AccountView.vue'), meta: { title: 'Compte' } },
    { path: '/:pathMatch(.*)*', name: 'not-found', component: () => import('@/views/NotFoundView.vue'), meta: { bare: true } },
  ],
})

/** Destination autorisée pour une route, ou null si l'accès est permis. */
export function redirectFor(to: RouteLocationNormalized): string | { name: string; query?: Record<string, string> } | null {
  const authStore = useAuthStore()
  if (to.name === 'account' && !authStore.isSignedIn) return { name: 'login' }
  const min = to.meta.minRole
  if (!min) return null
  if (!authStore.isSignedIn) return { name: 'login', query: { redirect: to.fullPath } }
  if (!authStore.hasRole(min)) return authStore.isMember ? { name: 'catalog' } : { name: 'showcase' }
  return null
}

router.beforeEach(async (to) => {
  const authStore = useAuthStore()
  await authStore.whenReady()
  // Un membre connecté arrive directement sur le catalogue.
  if (to.name === 'showcase' && authStore.isMember && to.query.vitrine === undefined) return { name: 'catalog' }
  if (to.name === 'login' && authStore.isSignedIn) return authStore.isMember ? { name: 'catalog' } : { name: 'showcase' }
  return redirectFor(to) ?? true
})

router.afterEach((to) => {
  document.title = to.meta.title ? `${to.meta.title} · Ex Libris` : 'Ex Libris · Bibliothèque familiale'
})

export default router
